import React from 'react';

import {BioentityAliasInfo} from 'constants/RegistryConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';

/**
 * Returns the BioentityAliasInfos which are owned by the Registry, i.e. have a registry collection owner
 *
 * @param {BioentitySummary} bioentity - The bioentity summary containing alias information
 * @returns {Array<BioentityAliasInfo>} A list of BioentityAliasInfo owned by the Registry
 */
const getRegistryIdentifierBioentityAliasInfos = function (
  bioentity: BioentitySummary
): Array<BioentityAliasInfo> {
  return bioentity.bioentityAliasInfos.filter(
    (bioentityAliasInfo) => bioentityAliasInfo.registryCollectionOwner != null
  );
};

/**
 * Returns the registry identifier alias for a given collection
 *
 * @param {Array<BioentityAliasInfo>} bioentityAliasInfos - List of bioentity alias information
 * @param {string | null | undefined} collection - The collection ID to match against
 * @returns {string | null} The alias string if found, null otherwise
 */
export function getRegistryIdentifier(
  bioentityAliasInfos: Array<BioentityAliasInfo>,
  collection: string | null | undefined
): string | null {
  const bioentityAliasInfo = bioentityAliasInfos.find(
    (bai) => (bai.registryCollectionOwner != null ? bai.registryCollectionOwner.id : undefined) === collection
  );
  return bioentityAliasInfo?.alias ?? null;
}

/**
 * Returns the BioentityAliasInfos which are not owned by the Registry, i.e. no registry collection owner
 *
 * @param {{bioentityAliasInfos: Array<BioentityAliasInfo>}} bioentity - The bioentity to filter alias infos from
 * @returns {Array<BioentityAliasInfo>} Array of BioentityAliasInfo that have no registry collection owner
 */
export function getNonRegistryIdentifierAliasInfos(bioentity: {
  bioentityAliasInfos: Array<BioentityAliasInfo>;
}): Array<BioentityAliasInfo> {
  return bioentity.bioentityAliasInfos.filter(({registryCollectionOwner}) => registryCollectionOwner == null);
}

/**
 * Returns the primary registry identifier for a bioentity, prioritizing the organization's registry
 * identifier, then falling back to the current user's registry identifier
 *
 * @param {BioentitySummary} bioentity - The bioentity summary
 * @returns {string | null} The primary registry identifier if found, null otherwise
 */
export function getPrimaryRegistryIdentifier(bioentity: BioentitySummary): string | null {
  const registryIdentifiers = getRegistryIdentifierBioentityAliasInfos(bioentity);
  if (registryIdentifiers.length === 0) {
    return null;
  }
  const orgRegistryIdentifier = getRegistryIdentifier(
    registryIdentifiers,
    bioentity.folder.source!.s_owner.id
  );
  if (orgRegistryIdentifier) {
    return orgRegistryIdentifier;
  }
  const currentUser = EntityStore.currentUser();
  if (currentUser) {
    const primaryRegistryIdentifier = getRegistryIdentifier(registryIdentifiers, currentUser.id);
    return primaryRegistryIdentifier;
  }
  return null;
}

/**
 * Returns JSX elements representing the various aliases of a bioentity, including the primary name,
 * registry identifier for the given collection, and other aliases
 *
 * @param {Array<BioentityAliasInfo>} aliases - List of bioentity alias information
 * @param {string} name - The primary name of the bioentity
 * @param {string | null | undefined} collection - The collection ID to find the registry identifier for
 * @returns {Array<JSX.Element>} Array of JSX span elements with appropriate styling
 */
export function getAliasComponents(
  aliases: Array<BioentityAliasInfo>,
  name: string,
  collection: string | null | undefined
): Array<JSX.Element> {
  let registryIdentifier: string | null;
  const components = [];
  if (name != null) {
    components.push(
      <span
        key={`name-${name}`}
        className="sidepanel-primaryLabel"
        {...getTestProps({component: 'Sidepanel', element: 'item', key: 'primaryLabel'})}
      >
        {`${name} `}
      </span>
    );
  }
  if (collection != null) {
    registryIdentifier = getRegistryIdentifier(aliases, collection);
    if (registryIdentifier != null) {
      components.push(
        <span key={`registryIdentifier-${registryIdentifier}`} className="sidepanel-secondaryLabel">
          {`${registryIdentifier} `}
        </span>
      );
    }
  }
  return components.concat(
    aliases
      .filter((a) => a.alias !== registryIdentifier && a.alias !== name)
      .map((alias) => (
        <span key={alias.alias} className="sidepanel-tertiaryLabel">
          {`${alias.alias} `}
        </span>
      ))
  );
}
